import { QueryResult, ViewConfig } from './view-config'

/**
 * CSV export for either result kind. Values are RAW (machine-friendly), not
 * display-formatted: dates stay ISO, numbers stay plain, arrays join with
 * ';', nulls become empty cells. Zero deps — Blob + anchor download.
 */

/** Quote a cell when it contains a comma, quote or newline (RFC 4180). */
const escapeCell = (cell: string): string =>
  /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell

/** Raw cell text: null/undefined → '', arrays → ';'-joined, else String(). */
const rawValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(';')
  return String(value)
}

const joinRows = (rows: string[][]): string =>
  rows.map((r) => r.map(escapeCell).join(',')).join('\n')

/**
 * Serialize a query result to CSV text.
 * - aggregate: group-key column + one column per metric, TOTAL row last
 * - list: config.columns as headers (raw field names), one row per deal
 */
export const toCsv = (config: ViewConfig, result: QueryResult): string => {
  if (result.kind === 'aggregate') {
    const metricLabels = (config.metrics ?? []).map((m) => m.label)
    const groupHeader =
      config.groupBy?.bucket === 'quarter'
        ? 'quarter'
        : String(config.groupBy?.field ?? 'group')
    const rows = [...result.groups, result.total].map((g) => [
      g.key,
      ...metricLabels.map((label) => rawValue(g.metrics[label])),
    ])
    return joinRows([[groupHeader, ...metricLabels], ...rows])
  }
  const columns = config.columns ?? []
  const rows = result.rows.map((row) => columns.map((f) => rawValue(row[f])))
  return joinRows([columns.map(String), ...rows])
}

/** `<view-name-slug>-<yyyy-mm-dd>.csv` */
export const csvFilename = (viewName: string): string => {
  const slug = viewName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  const date = new Date().toISOString().slice(0, 10)
  return `${slug}-${date}.csv`
}

/** Trigger a browser download of CSV text. */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
